// Dev-only fixtures for the access funnel (arc 101 slice 01): the waitlist inbox
// (`/v1/admin/access-requests*`) and the platform invitations it mints
// (`/v1/admin/invitations*`). Split out of the console fixtures on the
// `admin-grants-mocks` precedent: a write plane of its own.
//
// STATEFUL, because every write RE-READS. An invite flips the application to
// `invited` and mints an invitation row the other list then shows; a decline
// stamps `declined`; a revoke parks the invitation; a resend rotates its expiry.
// A stateless echo would restore the pending row on the next GET and make the
// e2e story lie. `resetAdminAccessMocks` is the reset seam (`./mock-state.ts`).
//
// THE ACCEPT LINK IS MINTED ONCE, in the invite's answer — the same posture as
// the real control: no listing here carries it either.

import type { AccessRequestView, InvitationView } from '@developerz/domain';
import type { AccessRequestProvisioned } from './admin-access-requests';
import { decidePendingInvitation } from './admin-invitation-mocks';
import { ApiError } from './api';
import { at } from './mock-session';
import { unhandledMockRoute } from './unhandled-mock-route';

/**
 * Four applications: every derived status has a row, and PENDING has two. The
 * second pending row is load-bearing, not padding: the inbox opens on the
 * `pending` filter, so with one pending row a single decision empties the view
 * and the story that declines one (`e2e/access-requests.spec.ts`) would be
 * reading the empty state, not a queue that still has work in it.
 */
const accessRequests: AccessRequestView[] = [
  {
    id: 'acr_mock01',
    email: 'grace@hopper.dev',
    name: 'Grace Hopper',
    org: 'Hopper Labs',
    note: 'Three repos, one of them a compiler. Want the maintainer on all of them.',
    source: 'waitlist',
    status: 'pending',
    invitation_id: null,
    decided_by_user_id: null,
    created_at_iso: at('2026-07-18'),
    updated_at_iso: at('2026-07-20'),
    decided_at_iso: null,
  },
  {
    id: 'acr_mock04',
    email: 'katherine@johnson.dev',
    name: 'Katherine Johnson',
    org: null,
    note: 'Solo maintainer of an orbital-mechanics library.',
    source: 'waitlist',
    status: 'pending',
    invitation_id: null,
    decided_by_user_id: null,
    created_at_iso: at('2026-07-16'),
    updated_at_iso: at('2026-07-16'),
    decided_at_iso: null,
  },
  {
    id: 'acr_mock02',
    email: 'linus@kernel.example',
    name: 'Linus T.',
    org: null,
    note: null,
    source: 'waitlist',
    status: 'invited',
    invitation_id: 'inv_mock01',
    decided_by_user_id: 'usr_dev',
    created_at_iso: at('2026-07-10'),
    updated_at_iso: at('2026-07-10'),
    decided_at_iso: at('2026-07-12'),
  },
  {
    id: 'acr_mock03',
    email: 'spam@example.invalid',
    name: 'Not A Person',
    org: 'SEO Growth Ltd',
    note: 'Backlinks?',
    source: 'waitlist',
    status: 'declined',
    invitation_id: null,
    decided_by_user_id: 'usr_dev',
    created_at_iso: at('2026-07-01'),
    updated_at_iso: at('2026-07-01'),
    decided_at_iso: at('2026-07-02'),
  },
];

/** One invitation per status; the pending one is the row the inbox's invite bound. */
const invitations: InvitationView[] = [
  {
    id: 'inv_mock01',
    kind: 'platform',
    account_id: null,
    role: null,
    email: 'linus@kernel.example',
    status: 'pending',
    invited_by_user_id: 'usr_dev',
    expires_at_iso: at('2026-08-11'),
    created_at_iso: at('2026-07-12'),
    accepted_at_iso: null,
    revoked_at_iso: null,
  },
  {
    id: 'inv_mock02',
    kind: 'platform',
    account_id: null,
    role: null,
    email: 'ada@developerz.ai',
    status: 'accepted',
    invited_by_user_id: 'usr_dev',
    expires_at_iso: at('2026-03-01'),
    created_at_iso: at('2026-02-01'),
    accepted_at_iso: at('2026-02-02'),
    revoked_at_iso: null,
  },
  {
    id: 'inv_mock03',
    kind: 'org_member',
    account_id: 'acc_devsample',
    role: 'member',
    email: 'former@developerz.ai',
    status: 'revoked',
    invited_by_user_id: 'usr_dev',
    expires_at_iso: at('2026-06-15'),
    created_at_iso: at('2026-06-01'),
    accepted_at_iso: null,
    revoked_at_iso: at('2026-06-03'),
  },
  {
    id: 'inv_mock04',
    kind: 'platform',
    account_id: null,
    role: null,
    email: 'late@example.com',
    status: 'expired',
    invited_by_user_id: 'usr_dev',
    expires_at_iso: at('2026-05-15'),
    created_at_iso: at('2026-05-01'),
    accepted_at_iso: null,
    revoked_at_iso: null,
  },
];

const ACCESS = '/v1/admin/access-requests';
const INVITATIONS = '/v1/admin/invitations';

/** `?filter=status:<value>` → the rows it names (`all` drops the fence). */
function filtered<T extends { status: string }>(rows: readonly T[], query: string): readonly T[] {
  const term = new URLSearchParams(query).get('filter') ?? 'status:all';
  const status = term.replace(/^status:/, '');
  return status === 'all' ? rows : rows.filter((r) => r.status === status);
}

const page = <T>(data: readonly T[]): Promise<unknown> =>
  Promise.resolve({ data, next_cursor: null });

const notFound = (what: string): Promise<never> =>
  Promise.reject(new ApiError(404, `no such ${what}`, undefined, `no such ${what}`));

/** `POST …/access-requests/:id/{invite,decline}` — the decision twins. */
function decideAccessRequest(id: string, verb: string, init?: RequestInit): Promise<unknown> {
  const i = accessRequests.findIndex((r) => r.id === id);
  const row = accessRequests[i];
  if (row === undefined) return notFound('access request');
  if (row.status !== 'pending') {
    return Promise.reject(
      new ApiError(409, 'already decided', undefined, `this application is already ${row.status}`),
    );
  }
  if (verb === 'decline') {
    accessRequests[i] = {
      ...row,
      status: 'declined',
      decided_by_user_id: 'usr_dev',
      decided_at_iso: at('2026-07-22'),
    };
    return Promise.resolve(accessRequests[i]);
  }
  if (verb !== 'invite') return notFound('route');
  const body = init?.body
    ? (JSON.parse(String(init.body)) as { ai_devs?: number; expires_in_days?: number | null })
    : {};
  const aiDevs = body.ai_devs ?? 5;
  // How long the GRANT lasts; `null` is ongoing (plan 102 slice 05), never the default.
  const expiresInDays = body.expires_in_days === undefined ? 30 : body.expires_in_days;
  const invitationId = `inv_new_${invitations.length + 1}`;
  invitations.unshift({
    id: invitationId,
    kind: 'platform',
    account_id: null,
    role: null,
    email: row.email,
    status: 'pending',
    invited_by_user_id: 'usr_dev',
    // The accept LINK's own life, not the grant's (`expiresInDays` is the grant).
    expires_at_iso: at('2026-08-21'),
    created_at_iso: at('2026-07-22'),
    accepted_at_iso: null,
    revoked_at_iso: null,
  });
  accessRequests[i] = {
    ...row,
    status: 'invited',
    invitation_id: invitationId,
    decided_by_user_id: 'usr_dev',
    decided_at_iso: at('2026-07-22'),
  };
  // The invite twin's answer (seat C's shape): the one-time accept link rides
  // here and nowhere else. `bundle.boxes` is the SERVER's derived budget
  // (`starterBoxBudget`: a free personal account's `ai_devs × SERVERS_PER_DEV`,
  // nothing added on top — 5 devs → 15). A FIXTURE number, not the rule: the rule
  // lives in `@developerz/billing` alone and no screen computes it.
  const invited = accessRequests[i] as AccessRequestView;
  const answer: AccessRequestProvisioned = {
    request: invited,
    bundle: { ai_devs: aiDevs, expires_in_days: expiresInDays, boxes: aiDevs * 3 },
    user_id: null,
    login: null,
    email: row.email,
    invitation: {
      invitationId,
      acceptUrl: `https://app.developerz.ai/invite?token=dev_inv_${invitationId}`,
      expiresAtIso: invitations[0]?.expires_at_iso ?? at('2026-08-21'),
      reissued: false,
      mailed: true,
    },
    approval: 'not_yet_registered',
    grants: [
      { grant_id: 'grn_mock_invite', kind: 'ai_devs', amount: aiDevs, expires_at_iso: null },
    ],
    audit_id: 'ev_mock_provision',
  };
  return Promise.resolve(answer);
}

/** `POST …/invitations/:id/{revoke,resend}`. Both refuse a settled row with a 409. */
function decideInvitation(id: string, verb: string): Promise<unknown> {
  const i = invitations.findIndex((r) => r.id === id);
  const row = invitations[i];
  if (row === undefined) return notFound('invitation');
  if (row.status !== 'pending') {
    return Promise.reject(
      new ApiError(409, 'not pending', undefined, `this invitation is already ${row.status}`),
    );
  }
  const decided = decidePendingInvitation(row, verb);
  if (decided === null) return notFound('route');
  invitations[i] = decided.row;
  return Promise.resolve(decided.answer);
}

/**
 * Handle a `/v1/admin/access-requests*` or `/v1/admin/invitations*` request, or
 * return `null` so `mockFetch` falls through to the rest of the admin console.
 */
export function adminAccessMock(
  path: string,
  method: string,
  init?: RequestInit,
): Promise<unknown> | null {
  const [bare = path, query = ''] = path.split('?');
  const under = (base: string): string | null =>
    bare === base ? '' : bare.startsWith(`${base}/`) ? bare.slice(base.length + 1) : null;

  const access = under(ACCESS);
  if (access !== null) {
    if (access === '' && method === 'GET') return page(filtered(accessRequests, query));
    const [id = '', verb = ''] = access.split('/');
    if (method === 'POST' && verb !== '') {
      return decideAccessRequest(decodeURIComponent(id), verb, init);
    }
    return unhandledMockRoute(method, path);
  }

  const invitation = under(INVITATIONS);
  if (invitation !== null) {
    if (invitation === '' && method === 'GET') return page(filtered(invitations, query));
    const [id = '', verb = ''] = invitation.split('/');
    if (method === 'POST' && verb !== '') return decideInvitation(decodeURIComponent(id), verb);
    return unhandledMockRoute(method, path);
  }

  return null;
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
const PRISTINE_ACCESS = accessRequests.map((row) => structuredClone(row));
const PRISTINE_INVITATIONS = invitations.map((row) => structuredClone(row));

export function resetAdminAccessMocks(): void {
  accessRequests.splice(
    0,
    accessRequests.length,
    ...PRISTINE_ACCESS.map((r) => structuredClone(r)),
  );
  invitations.splice(0, invitations.length, ...PRISTINE_INVITATIONS.map((r) => structuredClone(r)));
}
